/**
 * Marketing-visit tracking — records a single "visit" event on any
 * public marketing entry point (Campaigns, Landing Pages, Affinity
 * registrations) so the Marketing → Acquisition dashboard can report
 * a full-funnel Visits → Signups → Orders → Revenue view.
 *
 * Reuses the first-touch UTM/referrer already captured by
 * `lib/acquisition.js` (localStorage `flow_acquisition_v1`) so a
 * repeat visit still carries the source that first brought the
 * visitor in.
 *
 * Never blocks or throws — a tracking failure must not break a
 * marketing page.
 */
import axios from 'axios';
import { API_BASE } from './apiBase';

const VISITOR_ID_KEY = 'flow_visitor_id_v1';
const SESSION_MARK_PREFIX = 'flow_visit_seen_';
const ACQUISITION_KEY = 'flow_acquisition_v1';

// Internal roles whose visits we never want to count. Mirrors the
// backend's INTERNAL_ROLES set — kept in sync manually.
const INTERNAL_ROLES = new Set(['admin', 'super_admin', 'customer_service']);

function readAcquisition() {
    try {
        const raw = localStorage.getItem(ACQUISITION_KEY);
        if (!raw) return {};
        return JSON.parse(raw) || {};
    } catch (_e) {
        return {};
    }
}

function ensureVisitorId() {
    try {
        let vid = localStorage.getItem(VISITOR_ID_KEY);
        if (!vid) {
            vid = (crypto?.randomUUID?.() || `v-${Date.now()}-${Math.random().toString(16).slice(2, 10)}`);
            localStorage.setItem(VISITOR_ID_KEY, vid);
        }
        return vid;
    } catch (_e) {
        return null;
    }
}

function shouldSkipForInternalUser() {
    try {
        const raw = localStorage.getItem('user');
        if (!raw) return false;
        const u = JSON.parse(raw);
        return INTERNAL_ROLES.has(u?.role);
    } catch (_e) {
        return false;
    }
}

/**
 * Fire-and-forget visit ping. Dedupes within a single browser session
 * so a client-side navigation that re-mounts the entry-point component
 * doesn't inflate the visit count.
 *
 * @param {"campaign"|"landing_page"|"affinity"} entryType
 * @param {string} entrySlug
 */
export function trackMarketingVisit(entryType, entrySlug) {
    try {
        if (!entryType || !entrySlug) return;
        if (shouldSkipForInternalUser()) return;

        const sessionKey = `${SESSION_MARK_PREFIX}${entryType}:${entrySlug}`;
        if (sessionStorage.getItem(sessionKey)) return;
        sessionStorage.setItem(sessionKey, '1');

        const acq = readAcquisition();
        const params = new URLSearchParams(window.location.search);
        const payload = {
            entry_type: entryType,
            entry_slug: entrySlug,
            path: window.location.pathname,
            visitor_id: ensureVisitorId(),
            // Prefer the current URL's UTM (present on the entry-point
            // page) and fall back to the persisted first-touch values.
            referrer: acq.referrer || document.referrer || null,
            utm_source: params.get('utm_source') || acq.utm_source || null,
            utm_medium: params.get('utm_medium') || acq.utm_medium || null,
            utm_campaign: params.get('utm_campaign') || acq.utm_campaign || null,
            utm_term: params.get('utm_term') || acq.utm_term || null,
            utm_content: params.get('utm_content') || acq.utm_content || null,
        };

        // 204 endpoint, no response body to consume. Ignore all errors.
        axios.post(`${API_BASE}/api/public/marketing/visit`, payload).catch(() => {});
    } catch (_e) {
        // Swallow — analytics must never break the page.
    }
}
